import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { refreshGptSlots } from 'client/utils/ads-runtime/refresh-gpt-slots';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';

import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';

import { NativeAd } from 'site-modules/shared/components/ad-unit/native-ad';
import { isRenderedAdUnit } from 'client/utils/ads';

import { CommonAdDefaultProps, CommonAdPropTypes } from 'site-modules/shared/components/ad-unit/ad-unit-prop-types';
import { RenderWhenViewable } from 'site-modules/shared/components/render-when-viewable/render-when-viewable';
import { getPwtEnabled } from 'client/utils/pwt/get-pwt-enabled-page';

import { collapseEmptyCarouselAdSlot } from './filmstrip-carousel/utils/filmstrip-carousel-utils';
import { FilmstripCarousel } from './filmstrip-carousel/filmstrip-carousel';

import './sponsored-cars.scss';

const MAX_AD_COUNT = 4;
const CREATIVE_ID = 'edm-entry-also-viewed-vehicles-sponsored-cars';

function SponsoredCarsUI({
  isMobile = false,
  wrapperClassName = '',
  showTopDividingLine = false,
  showBottomDividingLine = true,
  showLargeArrows = false,
  pwtEnabled = false,
  verticalOffset = '100%',
  adsCustomTargeting = CommonAdDefaultProps.customTargeting,
  trackLoading = false,
  inlineStyle = null,
  headingClass = 'heading-3',
  showStackedAds = false,
}) {
  const containerRef = useRef();
  const totalAdCalls = useRef(0);
  const totalAdsLoaded = useRef([]);
  const totalAdCallsRequested = useRef(0);
  const adCallsRequested = useRef([]);

  const [showFilmstrip, setShowFilmstrip] = useState(false);

  const handleSlotRenderEnd = useCallback(
    slotObj => {
      const isLoaded = isRenderedAdUnit(slotObj);
      const adSlotClass = '.slick-slide';

      totalAdCalls.current += 1;
      if (isLoaded) {
        totalAdsLoaded.current.push(slotObj.slot.getSlotElementId());
      }

      if (totalAdCalls.current === MAX_AD_COUNT) {
        if (trackLoading) {
          EventToolbox.fireTrackAction({
            event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
            event_data: {
              action_name: TrackingConstant.ACTION_SHOW_CONTENT,
              action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
              subaction_name: 'sponsored_cars_displayed',
              creative_id: CREATIVE_ID,
              value: !!totalAdsLoaded.current.length,
            },
          });
        }
        setShowFilmstrip(totalAdsLoaded.current.length > 0);
        if (!showStackedAds) collapseEmptyCarouselAdSlot(containerRef.current, adSlotClass, totalAdsLoaded.current);
        totalAdCalls.current = 0;
        totalAdsLoaded.current = [];
      }
    },
    [trackLoading, showStackedAds]
  );

  const onSlotRegister = useCallback(({ slotId }) => {
    adCallsRequested.current.push(slotId);
    totalAdCallsRequested.current += 1;
    // window.PWT?.adsPwtLoaded - see client/utils/pwt/get-pwt-bids.js
    if (totalAdCallsRequested.current % MAX_AD_COUNT === 0 && window.PWT?.adsPwtLoaded) {
      setTimeout(() => {
        refreshGptSlots(...adCallsRequested.current);
      }, 100);
    }
  }, []);

  const pwtProps = pwtEnabled
    ? {
        skipPwtRefresh: true,
        onSlotRegister,
      }
    : {};

  const renderDefaultFilmstripAdUnits = () =>
    new Array(MAX_AD_COUNT).fill().map((_, index) => (
      <div key={`native-ad-${index + 1}`} className="slick-slide" data-index={index + 1}>
        <NativeAd
          nativeStyle="filmstrip"
          requestGroupSize={MAX_AD_COUNT}
          position={`${index + 1}`}
          slotRenderEndListener={handleSlotRenderEnd}
          customTargeting={adsCustomTargeting}
          {...pwtProps}
        />
      </div>
    ));

  const renderStackedFilmstripAdUnits = () =>
    new Array(MAX_AD_COUNT).fill().map((_, index) => (
      <Col
        xs={6}
        key={`native-ad-${index + 1}`}
        data-index={index}
        className={classNames('pe-0 pb-0_75', {
          'ps-0_5': index % 2 === 0,
        })}
      >
        <NativeAd
          nativeStyle="filmstrip"
          requestGroupSize={MAX_AD_COUNT}
          position={`${index + 1}`}
          slotRenderEndListener={handleSlotRenderEnd}
          customTargeting={adsCustomTargeting}
          {...pwtProps}
        />
      </Col>
    ));

  const renderStackedAdWrapper = <Row>{renderStackedFilmstripAdUnits()}</Row>;

  const renderDefaultFilmstripCarousel = (
    <FilmstripCarousel noAdsRefresh totalAdCount={MAX_AD_COUNT} showArrows={showFilmstrip}>
      {renderDefaultFilmstripAdUnits()}
    </FilmstripCarousel>
  );

  const sponsoredContent = (
    <div
      className={classNames('sponsored-cars', wrapperClassName, {
        'invisible d-none': !showFilmstrip,
        'is-wired': !isMobile,
        'is-mobile': isMobile,
        'show-large-arrows': showLargeArrows,
        'is-stacked-ads': showStackedAds,
      })}
      data-tracking-parent={CREATIVE_ID}
      ref={containerRef}
      style={inlineStyle}
    >
      {showTopDividingLine && <hr className="mb-3_5" />}
      <div
        className={classNames('mb-1_25 mb-md-2 text-start', headingClass, {
          'text-cool-gray-10': showStackedAds,
        })}
      >
        Consider these recommendations
        {showStackedAds && (
          <sup className="size-12 fw-medium text-cool-gray-50" style={{ top: '-0.5rem', left: '0.25rem' }}>
            Ad
          </sup>
        )}
      </div>
      {showStackedAds ? renderStackedAdWrapper : renderDefaultFilmstripCarousel}
      {showBottomDividingLine && <hr className="mt-2 mb-3_5" />}
    </div>
  );

  return (
    <RenderWhenViewable verticalOffset={verticalOffset} placeholder={<div className="sponsored-cars" />}>
      {sponsoredContent}
    </RenderWhenViewable>
  );
}

SponsoredCarsUI.propTypes = {
  adsCustomTargeting: CommonAdPropTypes.customTargeting,
  isMobile: PropTypes.bool,
  wrapperClassName: PropTypes.string,
  showTopDividingLine: PropTypes.bool,
  showBottomDividingLine: PropTypes.bool,
  showLargeArrows: PropTypes.bool,
  pwtEnabled: PropTypes.bool,
  trackLoading: PropTypes.bool,
  verticalOffset: PropTypes.string,
  inlineStyle: PropTypes.shape({}),
  headingClass: PropTypes.string,
  showStackedAds: PropTypes.bool,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
  pwtEnabled: getPwtEnabled(state),
});

export const SponsoredCars = connect(mapStateToProps)(SponsoredCarsUI);
